import { useState } from "react";
import type { ProductDetail } from "@/mocks/products";

interface ProductFormModalProps {
  product?: ProductDetail;
  onClose: () => void;
  onSave: () => void;
}

export default function ProductFormModal({ product, onClose, onSave }: ProductFormModalProps) {
  const isEditing = !!product;
  const [name, setName] = useState(product?.nameEn || "");
  const [price, setPrice] = useState(product?.price.toString() || "");
  const [comparePrice, setComparePrice] = useState(product?.compareAtPrice?.toString() || "");
  const [category, setCategory] = useState(product?.categoryId || "cat-1");
  const [stock, setStock] = useState(product?.stockCount.toString() || "");
  const [inStock, setInStock] = useState(product?.inStock ?? true);

  const handleSave = () => {
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-background-200/70">
        <div className="sticky top-0 bg-background-50 px-6 py-4 border-b border-background-200/70 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-lg font-heading font-bold text-foreground-950">
            {isEditing ? "Edit Product" : "Add New Product"}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Product name */}
          <div>
            <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">Product Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter product name"
              className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
            />
          </div>

          {/* Price row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">Price ($)</label>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="29.99"
                step="0.01"
                min="0"
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">Compare at Price</label>
              <input
                type="number"
                value={comparePrice}
                onChange={(e) => setComparePrice(e.target.value)}
                placeholder="39.99"
                step="0.01"
                min="0"
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
          </div>

          {/* Category + Stock */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300"
              >
                <option value="cat-1">Electronics</option>
                <option value="cat-2">Fashion</option>
                <option value="cat-3">Home & Living</option>
                <option value="cat-4">Beauty</option>
                <option value="cat-5">Sports & Outdoors</option>
                <option value="cat-6">Kitchen & Dining</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-2">Stock Quantity</label>
              <input
                type="number"
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                placeholder="100"
                min="0"
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
            </div>
          </div>

          {/* Status toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-foreground-900">Product Status</p>
              <p className="text-xs text-foreground-500 mt-0.5">Toggle product visibility in the store</p>
            </div>
            <button
              onClick={() => setInStock(!inStock)}
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                inStock ? "bg-accent-500" : "bg-foreground-300"
              }`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                inStock ? "translate-x-[22px]" : "translate-x-0.5"
              }`}></span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 px-6 py-4 border-t border-background-200/70 rounded-b-2xl flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {isEditing ? "Save Changes" : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
}